"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.debitLine = debitLine;
exports.creditLine = creditLine;
exports.salesInvoiceIntent = salesInvoiceIntent;
exports.customerReceiptIntent = customerReceiptIntent;
exports.customerAdvanceIntent = customerAdvanceIntent;
exports.advanceRecognitionIntent = advanceRecognitionIntent;
exports.purchaseInvoiceIntent = purchaseInvoiceIntent;
exports.supplierPaymentIntent = supplierPaymentIntent;
exports.treasuryTransferIntent = treasuryTransferIntent;
exports.prepaidPaymentIntent = prepaidPaymentIntent;
exports.prepaidAmortizationIntent = prepaidAmortizationIntent;
exports.depreciationIntent = depreciationIntent;
exports.stockReceiptPaidIntent = stockReceiptPaidIntent;
exports.stockIssueIntent = stockIssueIntent;
exports.salesCreditNoteIntent = salesCreditNoteIntent;
const money_1 = require("../money");
/** سطر مدين بعملة الشركة. */
function debitLine(accountCode, amount, currency, options = {}) {
    const value = money_1.Money.of(amount);
    return {
        accountCode,
        debit: value,
        credit: money_1.Money.zero(),
        currency,
        amountCurrency: value,
        currencyRate: money_1.Money.of('1'),
        ...options,
    };
}
/** سطر دائن بعملة الشركة. */
function creditLine(accountCode, amount, currency, options = {}) {
    const value = money_1.Money.of(amount);
    return {
        accountCode,
        debit: money_1.Money.zero(),
        credit: value,
        currency,
        amountCurrency: value,
        currencyRate: money_1.Money.of('1'),
        ...options,
    };
}
function intent(ctx, header, lines) {
    return {
        companyId: ctx.companyId,
        sourceType: header.sourceType,
        sourceId: header.sourceId,
        postingDate: header.postingDate,
        idempotencyKey: header.idempotencyKey,
        reference: header.reference ?? null,
        memo: header.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
/** فاتورة خدمة آجلة: مدين العملاء بالإجمالى، دائن الإيراد بالصافى وضريبة المخرجات. */
function salesInvoiceIntent(ctx, input) {
    const net = money_1.Money.of(input.net);
    const taxAmount = input.tax ? money_1.Money.of(input.tax.amount) : money_1.Money.zero();
    const gross = net.add(taxAmount);
    const counterparty = { type: 'customer', id: input.customerId };
    const lines = [
        debitLine(ctx.accounts.receivable, gross, ctx.currency, {
            counterparty,
            dimensions: input.dimensions ?? null,
        }),
        creditLine(input.revenueAccount ?? ctx.accounts.serviceRevenue, net, ctx.currency, {
            dimensions: input.dimensions ?? null,
        }),
    ];
    if (input.tax && taxAmount.isPositive()) {
        lines.push(creditLine(ctx.accounts.outputTax, taxAmount, ctx.currency, { tax: input.tax.snapshot }));
    }
    return intent(ctx, { ...input, sourceType: 'sales_invoice' }, lines);
}
/** تحصيل من عميل: مدين النقدية، دائن العملاء. لا إيراد جديد. */
function customerReceiptIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const target = input.into === 'bank' ? ctx.accounts.bank : ctx.accounts.cashbox;
    const lines = [
        debitLine(target, amount, ctx.currency, {
            cashboxId: input.into === 'cashbox' ? (input.cashboxId ?? null) : null,
            bankAccountId: input.into === 'bank' ? (input.bankAccountId ?? null) : null,
        }),
        creditLine(ctx.accounts.receivable, amount, ctx.currency, {
            counterparty: {
                type: 'customer',
                id: input.customerId,
                allocationRef: input.allocateTo ?? null,
            },
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'customer_receipt' }, lines);
}
/** قبض مقدم من عميل: مدين النقدية، دائن مقدمات العملاء. لا إيراد حتى التحقق. */
function customerAdvanceIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const target = input.into === 'bank' ? ctx.accounts.bank : ctx.accounts.cashbox;
    const lines = [
        debitLine(target, amount, ctx.currency, {
            cashboxId: input.into === 'cashbox' ? (input.cashboxId ?? null) : null,
            bankAccountId: input.into === 'bank' ? (input.bankAccountId ?? null) : null,
        }),
        creditLine(ctx.accounts.customerAdvance, amount, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId },
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'customer_advance' }, lines);
}
/** أداء الخدمة من مقدم: مدين مقدمات العملاء، دائن الإيراد. */
function advanceRecognitionIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const lines = [
        debitLine(ctx.accounts.customerAdvance, amount, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId },
        }),
        creditLine(input.revenueAccount ?? ctx.accounts.serviceRevenue, amount, ctx.currency, {
            dimensions: input.dimensions ?? null,
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'advance_recognition' }, lines);
}
/** فاتورة مورد آجلة: مدين المصروف وضريبة المدخلات، دائن الموردون. */
function purchaseInvoiceIntent(ctx, input) {
    const net = money_1.Money.of(input.net);
    const taxAmount = input.tax ? money_1.Money.of(input.tax.amount) : money_1.Money.zero();
    const gross = net.add(taxAmount);
    const lines = [
        debitLine(input.debitAccount ?? ctx.accounts.expense, net, ctx.currency, {
            dimensions: input.dimensions ?? null,
        }),
    ];
    if (input.tax && taxAmount.isPositive()) {
        lines.push(debitLine(ctx.accounts.inputTax, taxAmount, ctx.currency, { tax: input.tax.snapshot }));
    }
    lines.push(creditLine(ctx.accounts.payable, gross, ctx.currency, {
        counterparty: { type: 'supplier', id: input.supplierId },
    }));
    return intent(ctx, { ...input, sourceType: 'purchase_invoice' }, lines);
}
/** سداد مورد: مدين الموردون، دائن النقدية. */
function supplierPaymentIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const source = input.from === 'bank' ? ctx.accounts.bank : ctx.accounts.cashbox;
    const lines = [
        debitLine(ctx.accounts.payable, amount, ctx.currency, {
            counterparty: {
                type: 'supplier',
                id: input.supplierId,
                allocationRef: input.allocateTo ?? null,
            },
        }),
        creditLine(source, amount, ctx.currency, {
            cashboxId: input.from === 'cashbox' ? (input.cashboxId ?? null) : null,
            bankAccountId: input.from === 'bank' ? (input.bankAccountId ?? null) : null,
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'supplier_payment' }, lines);
}
/** تحويل بين خزينة وبنك: لا إيراد ولا مصروف، مرجع تحويل إلزامى. */
function treasuryTransferIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const lines = [
        debitLine(input.toAccount, amount, ctx.currency, { cashboxId: input.toCashboxId ?? null }),
        creditLine(input.fromAccount, amount, ctx.currency, { cashboxId: input.fromCashboxId ?? null }),
    ];
    return intent(ctx, { ...input, sourceType: 'treasury_transfer' }, lines);
}
/** دفع مصروف مقدم: مدين المصروفات المقدمة، دائن النقدية. */
function prepaidPaymentIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const source = input.from === 'cashbox' ? ctx.accounts.cashbox : ctx.accounts.bank;
    const lines = [
        debitLine(ctx.accounts.prepaid, amount, ctx.currency, {
            dimensions: input.dimensions ?? null,
        }),
        creditLine(source, amount, ctx.currency, {
            cashboxId: input.from === 'cashbox' ? (input.cashboxId ?? null) : null,
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'prepaid_payment' }, lines);
}
/** تحميل قسط من مصروف مقدم: مدين المصروف، دائن المصروفات المقدمة. */
function prepaidAmortizationIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const lines = [
        debitLine(input.expenseAccount, amount, ctx.currency, {
            dimensions: input.dimensions ?? null,
        }),
        creditLine(ctx.accounts.prepaid, amount, ctx.currency),
    ];
    return intent(ctx, { ...input, sourceType: 'prepaid_amortization' }, lines);
}
/** إهلاك دورى: مدين مصروف الإهلاك، دائن مجمع الإهلاك. لا سالب على الأصل الخام. */
function depreciationIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const lines = [
        debitLine(ctx.accounts.depreciationExpense, amount, ctx.currency),
        creditLine(ctx.accounts.accumulatedDepreciation, amount, ctx.currency, {
            memo: input.assetId ?? null,
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'depreciation' }, lines);
}
/** استلام مخزون مدفوع: مدين المخزون، دائن النقدية. */
function stockReceiptPaidIntent(ctx, input) {
    const amount = money_1.Money.of(input.amount);
    const source = input.from === 'cashbox' ? ctx.accounts.cashbox : ctx.accounts.bank;
    const lines = [
        debitLine(ctx.accounts.inventory, amount, ctx.currency),
        creditLine(source, amount, ctx.currency, {
            cashboxId: input.from === 'cashbox' ? (input.cashboxId ?? null) : null,
        }),
    ];
    return intent(ctx, { ...input, sourceType: 'stock_receipt' }, lines);
}
/** صرف مخزون بالتكلفة: مدين تكلفة البضاعة المباعة، دائن المخزون. الإيراد قيد منفصل. */
function stockIssueIntent(ctx, input) {
    const cost = money_1.Money.of(input.cost);
    const lines = [
        debitLine(ctx.accounts.cogs, cost, ctx.currency, { dimensions: input.dimensions ?? null }),
        creditLine(ctx.accounts.inventory, cost, ctx.currency),
    ];
    return intent(ctx, { ...input, sourceType: 'stock_issue' }, lines);
}
/** إشعار دائن: مدين مردودات المبيعات وضريبة المخرجات، دائن العملاء أو النقدية. */
function salesCreditNoteIntent(ctx, input) {
    const net = money_1.Money.of(input.net);
    const taxAmount = input.tax ? money_1.Money.of(input.tax.amount) : money_1.Money.zero();
    const gross = net.add(taxAmount);
    const settleTo = input.settleTo ?? 'receivable';
    const creditAccount = settleTo === 'receivable'
        ? ctx.accounts.receivable
        : settleTo === 'bank'
            ? ctx.accounts.bank
            : ctx.accounts.cashbox;
    const lines = [debitLine(ctx.accounts.salesReturns, net, ctx.currency)];
    if (input.tax && taxAmount.isPositive()) {
        lines.push(debitLine(ctx.accounts.outputTax, taxAmount, ctx.currency, { tax: input.tax.snapshot }));
    }
    lines.push(creditLine(creditAccount, gross, ctx.currency, {
        counterparty: settleTo === 'receivable' ? { type: 'customer', id: input.customerId } : undefined,
        cashboxId: settleTo === 'cashbox' ? (input.cashboxId ?? null) : null,
    }));
    return intent(ctx, { ...input, sourceType: 'sales_credit_note' }, lines);
}
//# sourceMappingURL=document-rules.js.map